import React, { useEffect, useState } from "react";
import { makeStyles, Typography, Box } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";

import useFormat from "../../../../hooks/useFormat";
import I18nText from "../../../../elements/I18nText";
import { SENSOR_STATUS } from "../../../../domain/sensor";
import SensorContentAnimation from "./SensorContentAnimation";

const Sensor = ({ sensor, color, width, zIndex }) => {
  const { formatNumber, formatUnit } = useFormat();

  const classes = makeStyles(theme => ({
    container: {
      zIndex,
      position: "relative",
      display: "flex",
      justifyContent: "center",
      width: "80%"
    },
    name: {
      position: "absolute",
      top: -50,
      width: "100%",
      textAlign: "center",
      color: theme.palette.text.primary,
      fontWeight: 500,
      fontSize: "0.95rem"
    },
    sensorContainer: {
      width,
      position: "relative",
      height: "calc(100% + 1px)",
      border: `1px solid ${alpha(theme.palette.grey[500], 0.5)}`,
      borderRadius: 8,
      backgroundColor: theme.palette.common.white,
      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
      overflow: "hidden"
    },
    content: {
      position: "absolute",
      bottom: 0,
      height: `${sensor.pct}%`,
      width: "100%",
      transition: "height 0.5s ease-out",
      overflow: "hidden"
    },
    liquidAnimation: {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "100%",
      overflow: "hidden"
    },
    contentText: {
      position: "absolute",
      bottom: 76,
      width: "100%",
      textAlign: "center",
      padding: theme.spacing(1),
      color: theme.palette.text.primary,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      zIndex: 10
    },
    valueDisplay: {
      fontWeight: 600,
      fontSize: "1rem",
      marginBottom: theme.spacing(0.5)
    },
    percentDisplay: {
      fontWeight: 500,
      fontSize: "0.9rem",
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.5)
    },
    fillHeightDisplay: {
      fontSize: "0.8rem",
      color: theme.palette.text.secondary,
      backgroundColor: alpha(theme.palette.grey[200], 0.7),
      borderRadius: 12,
      padding: "2px 8px"
    },
    errorText: {
      position: "absolute",
      bottom: 76,
      width: "100%",
      textAlign: "center",
      padding: theme.spacing(1),
      color: theme.palette.error.main,
      display: "flex",
      flexDirection: "column",
      alignItems: "center"
    },
    errorTitle: {
      fontWeight: 600,
      fontSize: "0.9rem",
      marginBottom: theme.spacing(0.5)
    },
    errorValue: {
      fontSize: "0.85rem",
      backgroundColor: alpha(theme.palette.error.light, 0.1),
      borderRadius: 4,
      padding: "2px 8px"
    },
    fillLimit: {
      display: "inlineBlock",
      position: "absolute",
      left: -10,
      top: `calc(${100 - sensor.fillLimit + "%"} - 7px)`,
      width: 0,
      height: 15,
      borderStyle: "solid",
      borderWidth: "7.5px 0 7.5px 13.0px",
      borderColor: `transparent transparent transparent ${theme.palette.warning.main}`,
      filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
      transition: "top 0.3s ease-out",
      zIndex: 20
    },
    fillLimitLine: {
      display: "inlineBlock",
      position: "absolute",
      left: 4,
      top: 100 - sensor.fillLimit + "%",
      width: "calc(100% - 5px)",
      height: 0,
      borderBottom: `1px dashed ${alpha(theme.palette.grey[500], 0.6)}`,
      transition: "top 0.3s ease-out",
      zIndex: 15
    },
    threshold: {
      display: "inlineBlock",
      position: "absolute",
      left: -10,
      top: `calc(${100 - sensor.threshold + "%"} - 7px)`,
      width: 0,
      height: 13,
      borderStyle: "solid",
      borderWidth: "7.5px 0 7.5px 13.0px",
      borderColor: `transparent transparent transparent ${theme.palette.error.main}`,
      filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
      transition: "top 0.3s ease-out",
      zIndex: 20
    },
    thresholdLine: {
      display: "inlineBlock",
      position: "absolute",
      left: 4,
      top: 100 - sensor.threshold + "%",
      width: "calc(100% - 5px)",
      height: 0,
      borderBottom: `1px dashed ${alpha(theme.palette.grey[500], 0.6)}`,
      transition: "top 0.3s ease-out",
      zIndex: 15
    }
  }))();

  // Get container width for wave calculation
  const containerWidthNum = typeof width === "string" ? 200 : width;

  return (
    <div className={classes.container}>
      <Typography className={classes.name} variant="subtitle2">
        {sensor.name}
      </Typography>
      <div className={classes.sensorContainer}>
        {sensor.status === SENSOR_STATUS.OK && (
          <div className={classes.content}>
            <SensorContentAnimation
              color={color}
              width={containerWidthNum}
              percentage={sensor.pct}
              isActive={sensor.pct > 0 && sensor.status === SENSOR_STATUS.OK}
              uniqueId={sensor.id || `sensor-${zIndex}`}
              amplitude={4}
              speed={0.02}
            />
          </div>
        )}

        <div className={classes.fillLimit}></div>
        <div className={classes.fillLimitLine}></div>
        <div className={classes.threshold}></div>
        <div className={classes.thresholdLine}></div>
        {sensor.status === SENSOR_STATUS.ERROR ? (
          <div className={classes.errorText}>
            {sensor.alarms.length > 0 ? (
              <>
                <Typography className={classes.errorTitle} variant="body2">
                  <I18nText>alarms.title</I18nText>
                </Typography>
                <Box className={classes.errorValue}>
                  {sensor.alarms[0].value}
                </Box>
              </>
            ) : (
              <Typography className={classes.errorTitle} variant="body2">
                <I18nText>sensors.status.error</I18nText>
              </Typography>
            )}
          </div>
        ) : (
          <div className={classes.contentText}>
            <Typography className={classes.valueDisplay} variant="body1">
              {formatNumber(sensor.value, 0)} {formatUnit(sensor.unit)}
            </Typography>
            <Typography className={classes.percentDisplay} variant="body2">
              {formatNumber(sensor.pct, 0)}%
            </Typography>
            {sensor.subType !== "level" && sensor.fillHeightValue !== null && (
              <Box className={classes.fillHeightDisplay}>
                {formatNumber(sensor.fillHeightValue, 1)} cm
              </Box>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sensor;
